import { useEffect, useState } from "react";
import api from "@/lib/api";
import { NUM } from "@/lib/format";
import { PageHeader, StatCard, Empty } from "@/components/Shared";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Activity, TrendingUp, Users2, HardHat, Shield, Package, Boxes, ClipboardList } from "lucide-react";

const actionLabels = {
  urun_ekle: "Ürün Ekleme", urun_duzenle: "Düzenleme", urun_sil: "Silme",
  stok_giris: "Stok Girişi", stok_cikis: "Stok Çıkışı", sayim: "Sayım", siparis_talep: "Sipariş Talebi",
};

export default function Performance() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/performance").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="animate-pulse h-64 bg-muted rounded-lg" />;

  const { staff, trend } = data;
  const totalToday = staff.reduce((s, u) => s + u.today, 0);
  const totalWeek = staff.reduce((s, u) => s + u.week, 0);
  const activeStaff = staff.filter((u) => u.total > 0).length;

  const lastActive = (iso) => { if (!iso) return "Hiç"; try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "-"; } };

  return (
    <div className="space-y-6 stagger-in" data-testid="performance-page">
      <PageHeader title="Personel Performansı" subtitle="Kullanıcıların günlük ve haftalık işlem özetleri" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Bugünkü İşlem" value={NUM(totalToday)} icon={Activity} tone="primary" />
        <StatCard label="Bu Hafta" value={NUM(totalWeek)} icon={TrendingUp} tone="blue" />
        <StatCard label="Aktif Kullanıcı" value={NUM(activeStaff)} icon={Users2} />
      </div>

      <div className="bg-card border rounded-lg p-6">
        <h3 className="font-head text-xl font-bold mb-6">Son 7 Gün İşlem Trendi</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="day" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: "hsl(var(--muted))" }} formatter={(v) => [`${v} işlem`, "Toplam"]} />
            <Bar dataKey="count" name="İşlem" fill="hsl(18 91% 52%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {staff.length === 0 ? (
        <Empty title="Veri yok" desc="Kullanıcı işlemleri kaydedildikçe burada görünecek." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {staff.map((u) => (
            <div key={u.user_id} className="bg-card border rounded-lg p-5" data-testid={`perf-${u.user_id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`h-11 w-11 rounded-full flex items-center justify-center font-bold ${u.role === "admin" ? "bg-primary/15 text-primary" : "bg-secondary/15 text-secondary"}`}>
                  {u.user_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-bold truncate">{u.user_name}</div>
                  <Badge variant={u.role === "admin" ? "default" : "secondary"} className="gap-1 text-[10px]">
                    {u.role === "admin" ? <><Shield className="h-3 w-3" />Yönetici</> : <><HardHat className="h-3 w-3" />Depo Müdürü</>}
                  </Badge>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  Son işlem<br /><span className="font-semibold text-foreground">{lastActive(u.last_active)}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                <Mini label="Bugün" value={u.today} tone="text-primary" />
                <Mini label="Bu Hafta" value={u.week} tone="text-secondary" />
                <Mini label="Toplam" value={u.total} />
              </div>

              <div className="space-y-1.5">
                {Object.keys(u.by_action).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">Henüz işlem yok</p>
                ) : (
                  Object.entries(u.by_action).sort((a, b) => b[1] - a[1]).map(([action, count]) => {
                    const max = Math.max(...Object.values(u.by_action));
                    return (
                      <div key={action} className="flex items-center gap-2 text-sm">
                        <span className="w-28 shrink-0 text-muted-foreground text-xs">{actionLabels[action] || action}</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${(count / max) * 100}%` }} />
                        </div>
                        <span className="w-8 text-right tabular-nums font-semibold">{count}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, tone = "" }) {
  return (
    <div className="bg-muted/40 rounded-lg p-2 text-center">
      <div className={`font-head font-black text-xl tabular-nums ${tone}`}>{NUM(value)}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
    </div>
  );
}
